/**
 * Token refresh scheduling: schedule refresh before expiry, retry on transient errors,
 * proactive refresh on 'token:refresh-required' event, and refresh on tab visibility.
 */

import { useEffect, useRef, useCallback, type MutableRefObject } from 'react';
import { calculateTokenRefreshTime, shouldRefreshTokenImmediately } from '@/utils/jwtUtils';
import { getHttpErrorKind } from '@/utils/httpErrors';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const RETRY_INTERVAL_MS = 30000;
const MAX_RETRY_DURATION_MS = 10 * 60 * 1000; // 10 minutes
const PROACTIVE_REFRESH_DEBOUNCE_MS = 2000;
const VISIBILITY_CHECK_DELAY_MS = 500;
const ONE_HOUR_MS = 60 * 60 * 1000;
/** Minutes before expiry to trigger proactive refresh (e.g. from request interceptor). */
const PROACTIVE_REFRESH_BEFORE_MINUTES = 2;

type TimeoutId = ReturnType<typeof setTimeout>;

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type LoginRef = MutableRefObject<(() => Promise<void>) | undefined>;
export type IsRefreshingRef = MutableRefObject<boolean>;

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Schedules token refresh before expiry, handles retries on transient errors,
 * listens for 'token:refresh-required' (debounced) and refreshes when tab becomes visible.
 * Syncs token to an internal ref for use in timeouts.
 *
 * @param microsoftToken - Current Microsoft id token (JWT); when null, scheduling is cleared.
 * @param loginRef - Ref to the current login function (used by timeouts to avoid stale closure).
 * @param isRefreshingRef - Optional ref shared with caller (e.g. set during silent token acquisition to avoid duplicate refresh).
 */
export function useTokenRefreshSchedule(
    microsoftToken: string | null,
    loginRef: LoginRef,
    isRefreshingRef?: IsRefreshingRef
) {
    const tokenRefreshTimeoutRef = useRef<TimeoutId | null>(null);
    const visibilityChangeTimeoutRef = useRef<TimeoutId | null>(null);
    const proactiveRefreshTimeoutRef = useRef<TimeoutId | null>(null);
    const isRefreshingRefInternal = useRef(false);
    const isRefreshingRefToUse = isRefreshingRef ?? isRefreshingRefInternal;
    const microsoftTokenRef = useRef<string | null>(null);

    const clearTokenRefreshTimeout = useCallback(() => {
        if (tokenRefreshTimeoutRef.current) {
            clearTimeout(tokenRefreshTimeoutRef.current);
            tokenRefreshTimeoutRef.current = null;
        }
    }, []);

    const logRefreshSchedule = useCallback((delayMsUntilRefresh: number) => {
        const minutesUntilRefresh = Math.round(delayMsUntilRefresh / 1000 / 60);
        console.log(`Token will be refreshed in ${minutesUntilRefresh} minutes`);
    }, []);

    const runTokenRefreshWithLogging = useCallback(async (errorLogMessage: string) => {
        if (!loginRef.current) return;
        try {
            await loginRef.current();
        } catch (error) {
            if (getHttpErrorKind(error) === 'auth') {
                console.log('Session expired or interaction required, redirecting to login.');
            } else {
                console.error(errorLogMessage, error);
            }
        }
    }, [loginRef]);

    /**
     * Schedules a single retry after RETRY_INTERVAL_MS. On transient failure, recurses until max duration.
     */
    const scheduleRetryRefresh = useCallback(
        (startTime: number, attemptNumber: number) => {
            const elapsed = Date.now() - startTime;
            if (elapsed >= MAX_RETRY_DURATION_MS) {
                console.warn('Max retry duration reached, stopping retry attempts');
                return;
            }

            console.warn(
                `Token refresh failed with transient error (attempt ${attemptNumber}). Will retry in ${RETRY_INTERVAL_MS / 1000}s...`
            );

            const attemptRefresh = async (
                attemptNum: number,
                opts: { logSuccess?: boolean; errorLabel?: string } = {}
            ) => {
                if (!loginRef.current) return;
                try {
                    await loginRef.current();
                    if (opts.logSuccess) console.log('Token refresh succeeded after retry');
                } catch (err) {
                    console.error(opts.errorLabel ?? 'Token refresh failed:', err);
                    const kind = getHttpErrorKind(err);
                    if (kind === 'transient' && Date.now() - startTime < MAX_RETRY_DURATION_MS) {
                        scheduleRetryRefresh(startTime, attemptNum + 1);
                    }
                }
            };

            tokenRefreshTimeoutRef.current = setTimeout(async () => {
                const latestToken = microsoftTokenRef.current;
                if (!latestToken) {
                    console.warn('Token no longer exists, stopping retry');
                    return;
                }
                if (shouldRefreshTokenImmediately(latestToken)) {
                    console.warn('Token is expiring soon, attempting immediate refresh...');
                    await attemptRefresh(attemptNumber, { errorLabel: 'Immediate token refresh failed' });
                    return;
                }
                await attemptRefresh(attemptNumber, {
                    logSuccess: true,
                    errorLabel: `Retry token refresh failed (attempt ${attemptNumber})`,
                });
            }, RETRY_INTERVAL_MS);
        },
        [loginRef]
    );

    /** Runs refresh on schedule; on transient error starts retry loop. */
    const runScheduledRefresh = useCallback(async () => {
        if (!loginRef.current) {
            console.warn('loginRef.current is undefined, cannot refresh token');
            return;
        }
        console.log('Refreshing token before expiration...');
        try {
            await loginRef.current();
        } catch (error) {
            const errorKind = getHttpErrorKind(error);
            console.error('Error calling login function:', error);
            const currentToken = microsoftTokenRef.current;
            if (errorKind === 'transient' && currentToken) {
                scheduleRetryRefresh(Date.now(), 1);
            } else {
                console.warn(
                    'Token refresh failed with non-transient error. User may need to login again.'
                );
            }
        }
    }, [loginRef, scheduleRetryRefresh]);

    const setupTokenRefresh = useCallback(
        (token: string) => {
            try {
                clearTokenRefreshTimeout();

                if (shouldRefreshTokenImmediately(token)) {
                    console.log('Token is expiring soon or already expired, refreshing immediately');
                    if (loginRef.current) {
                        runTokenRefreshWithLogging('Error refreshing token immediately:');
                    } else {
                        console.warn('loginRef.current is undefined, cannot refresh token immediately');
                    }
                    return;
                }

                const refreshTime = calculateTokenRefreshTime(token);
                logRefreshSchedule(refreshTime);

                tokenRefreshTimeoutRef.current = setTimeout(() => {
                    tokenRefreshTimeoutRef.current = null;
                    runScheduledRefresh();
                }, refreshTime);
            } catch (error) {
                console.error('Error setting up token refresh:', error);
            }
        },
        [
            clearTokenRefreshTimeout,
            logRefreshSchedule,
            runTokenRefreshWithLogging,
            runScheduledRefresh,
            loginRef,
        ]
    );

    // Sync ref and schedule when token changes; clear when token is null.
    useEffect(() => {
        microsoftTokenRef.current = microsoftToken;
        if (microsoftToken) {
            setupTokenRefresh(microsoftToken);
        } else {
            clearTokenRefreshTimeout();
        }
        return () => clearTokenRefreshTimeout();
    }, [microsoftToken, setupTokenRefresh, clearTokenRefreshTimeout]);

    // Proactive refresh: 'token:refresh-required' (e.g. from request interceptor); debounced, only if expiring soon.
    useEffect(() => {
        const handleProactiveRefresh = () => {
            if (proactiveRefreshTimeoutRef.current) {
                clearTimeout(proactiveRefreshTimeoutRef.current);
                proactiveRefreshTimeoutRef.current = null;
            }

            proactiveRefreshTimeoutRef.current = setTimeout(async () => {
                const currentToken = microsoftTokenRef.current;

                if (isRefreshingRefToUse.current) {
                    console.log('Token refresh already in progress, skipping proactive refresh');
                    return;
                }

                if (currentToken && loginRef.current) {
                    try {
                        if (shouldRefreshTokenImmediately(currentToken, PROACTIVE_REFRESH_BEFORE_MINUTES)) {
                            console.log('Proactive token refresh triggered from request interceptor');
                            isRefreshingRefToUse.current = true;
                            try {
                                await runTokenRefreshWithLogging('Error in proactive token refresh:');
                                console.log('Proactive token refresh succeeded');
                            } finally {
                                isRefreshingRefToUse.current = false;
                            }
                        } else {
                            console.log('Token still valid, skipping proactive refresh');
                        }
                    } catch (error) {
                        console.error('Error checking token expiration in proactive refresh:', error);
                        isRefreshingRefToUse.current = false;
                    }
                }
            }, PROACTIVE_REFRESH_DEBOUNCE_MS);
        };

        window.addEventListener('token:refresh-required', handleProactiveRefresh as EventListener);

        return () => {
            window.removeEventListener('token:refresh-required', handleProactiveRefresh as EventListener);
            if (proactiveRefreshTimeoutRef.current) {
                clearTimeout(proactiveRefreshTimeoutRef.current);
                proactiveRefreshTimeoutRef.current = null;
            }
        };
    }, [runTokenRefreshWithLogging, isRefreshingRefToUse, loginRef]);

    // Visibility: when tab becomes visible, refresh immediately if expiring soon; else reschedule if within 1 hour.
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;

            if (visibilityChangeTimeoutRef.current) {
                clearTimeout(visibilityChangeTimeoutRef.current);
                visibilityChangeTimeoutRef.current = null;
            }

            visibilityChangeTimeoutRef.current = setTimeout(async () => {
                const currentToken = microsoftTokenRef.current;

                if (!currentToken) return;
                try {
                    if (shouldRefreshTokenImmediately(currentToken)) {
                        console.log(
                            'Tab became active and token is expiring soon, refreshing immediately...'
                        );
                        await runTokenRefreshWithLogging(
                            'Error refreshing token when tab became active:'
                        );
                    } else {
                        const refreshTime = calculateTokenRefreshTime(currentToken);
                        if (refreshTime > 0 && refreshTime < ONE_HOUR_MS) {
                            console.log('Tab became active.');
                            logRefreshSchedule(refreshTime);
                            clearTokenRefreshTimeout();
                            tokenRefreshTimeoutRef.current = setTimeout(async () => {
                                tokenRefreshTimeoutRef.current = null;
                                console.log('Refreshing token after tab became active...');
                                await runTokenRefreshWithLogging(
                                    'Error refreshing token after tab became active:'
                                );
                            }, refreshTime);
                        }
                    }
                } catch (error) {
                    console.error('Error checking token expiration on visibility change:', error);
                }
            }, VISIBILITY_CHECK_DELAY_MS);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (visibilityChangeTimeoutRef.current) {
                clearTimeout(visibilityChangeTimeoutRef.current);
                visibilityChangeTimeoutRef.current = null;
            }
        };
    }, [
        clearTokenRefreshTimeout,
        logRefreshSchedule,
        runTokenRefreshWithLogging,
    ]);
}
